// stores/mbtiStore.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type { MbtiResult as MbtiResultPayload } from '~/types/NotificationsHub'
import type { PersonalityStatusDto } from '~/types/PersonalityAnalysis'
import { useFrontApi } from '~/composables/useFrontApi'
import { useAnalysisStore } from '~/stores/analysisStore'
import axios from 'axios'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'

// ── Types ─────────────────────────────────────────────────

type StoreStatus = 'idle' | 'loading' | 'ready' | 'error'
export type TestState = 'idle' | 'progress' | 'done'

export interface MbtiTrait {
  label: string
  desc: string
}

interface MbtiForce {
  pct: number
  name: string
  desc: string
}

interface FetchPersonalityStatusOptions {
  revealReportIfComplete?: boolean
}

/**
 * Extrait le code MBTI depuis `GET /api/Profile/personality/status`.
 * @param {PersonalityStatusDto} data - Corps JSON de la réponse status.
 * @returns {string | null} Code MBTI prêt à afficher, ou `null` si le test n'est pas terminé.
 */
function mbtiCodeFromPersonalityStatus(data: PersonalityStatusDto): string | null {
  const mbtiCode: string = data.personality?.mbti?.type?.trim() ?? ''
  return mbtiCode === '' ? null : mbtiCode
}

// Shape passée à setMbtiResult() depuis le composant de test
interface MbtiResult {
  mbti: string
  traits?: MbtiTrait[] | null
  forces?: MbtiForce[] | null
  shadow?: string[] | null
  shadowNote?: string | null
}

// ── Store ──────────────────────────────────────────────────

export const useMbtiStore = defineStore('mbti', () => {
  // ── State ────────────────────────────────────────────────
  const TOTAL_QUESTIONS = 11
  const MAX_ANALYSIS_WAIT_MS: number = 2 * 60 * 1000

  const mbti: Ref<string | null> = ref(null)
  const currentQuestionId: Ref<number | null> = ref(null)
  const traits: Ref<MbtiTrait[] | null> = ref(null)
  const forces: Ref<MbtiForce[] | null> = ref(null)
  const shadow: Ref<string[] | null> = ref(null)
  const shadowNote: Ref<string | null> = ref(null)
  const status: Ref<StoreStatus> = ref('idle')
  const isPersonalityReportPending: Ref<boolean> = ref(false)
  const analysisPending: Ref<boolean> = ref(false)

  let controller: AbortController | null = null
  let analysisPendingTimeoutId: ReturnType<typeof setTimeout> | null = null

  // ── Computed ─────────────────────────────────────────────

  const progressPct: ComputedRef<number> = computed(() => {
    if (!currentQuestionId.value) return 0
    return Math.round((currentQuestionId.value / TOTAL_QUESTIONS) * 100)
  })

  const testState: ComputedRef<TestState> = computed(() => {
    if (mbti.value) return 'done'
    if (currentQuestionId.value) return 'progress'
    return 'idle'
  })

  // ── Actions ──────────────────────────────────────────────

  /**
   * Fetch le statut du test et les données enrichies si mbti connu.
   * @param {FetchPersonalityStatusOptions} [options] - `revealReportIfComplete` ouvre le rapport si MBTI présent.
   * @returns {Promise<void>}
   */
  async function fetch(options?: FetchPersonalityStatusOptions): Promise<void> {
    controller?.abort()
    const activeController: AbortController = new AbortController()
    controller = activeController
    status.value = 'loading'

    try {
      const res = await useFrontApi().get<PersonalityStatusDto>('/api/Profile/personality/status', {
        signal: activeController.signal,
      })

      // FrontApi ne renvoie pas la question courante (progression du test sans source REST, cf. W4).
      currentQuestionId.value = null
      mbti.value = mbtiCodeFromPersonalityStatus(res.data)
      setAnalysisPending(res.data.personality?.mbti?.analysisPending ?? false)

      status.value = 'ready'

      if (options?.revealReportIfComplete) {
        tryRevealPersonalityReport()
      }

      clearPersonalityReportPendingIfReady()
    } catch (e) {
      if (axios.isCancel(e) || (e as Error).name === 'AbortError') {
        if (controller === activeController) status.value = 'idle'
        return
      }
      status.value = 'error'
      throw e
    }
  }

  /**
   * Ouvre le panneau rapport si le type MBTI est disponible (fin de test).
   * @returns {void}
   */
  function tryRevealPersonalityReport(): void {
    if ((mbti.value?.trim() ?? '') === '') return
    if (!isPersonalityReportPending.value) {
      isPersonalityReportPending.value = true
    }
    clearPersonalityReportPendingIfReady()
  }

  /**
   * Masque le loader dès que le rapport est exploitable (données présentes).
   * @returns {void}
   */
  function clearPersonalityReportPendingIfReady(): void {
    if (!isPersonalityReportPending.value) return

    const analysisStore = useAnalysisStore()
    const mbtiCode: string =
      mbti.value?.trim() || analysisStore.personalityAnalysis?.mbti?.type?.trim() || ''
    if (mbtiCode === '') return

    const hasReportContent: boolean = analysisStore.traits.length > 0

    if (hasReportContent) {
      isPersonalityReportPending.value = false
    }
  }

  /**
   * Met à jour la question courante (appelé depuis le composant de test)
   * @param {number} questionId Identifiant (index) de la question courante.
   * @returns {void}
   */
  function setCurrentQuestion(questionId: number): void {
    currentQuestionId.value = questionId
    const { track } = useTracking()
    track(TRACKING_EVENTS.MBTI_TEST_PROGRESSED, {
      questionId,
      progressPct: Math.round((questionId / TOTAL_QUESTIONS) * 100),
    })
  }

  /**
   * Appelé quand le test est terminé et que le back retourne le profil
   * @param {MbtiResult} result Résultat du test (profil + éléments optionnels).
   */
  function setMbtiResult(result: MbtiResult): void {
    mbti.value = result.mbti
    traits.value = result.traits ?? null
    forces.value = result.forces ?? null
    shadow.value = result.shadow ?? null
    shadowNote.value = result.shadowNote ?? null
    currentQuestionId.value = TOTAL_QUESTIONS

    // Résultat MBTI affiché : moment de décrochage produit à surveiller (replays).
    const { track, setPersonProperties } = useTracking()
    track(TRACKING_EVENTS.MBTI_RESULT_VIEWED, { mbtiType: result.mbti })
    setPersonProperties({ mbti_type: result.mbti })
  }

  /**
   * Reprend un type MBTI déjà calculé pour afficher le panneau Moi.
   * @param {string} mbtiCode - Code MBTI issu de l'analyse (ex. INTJ).
   */
  function hydrateFromAnalysis(mbtiCode: string): void {
    const normalizedMbtiCode: string = mbtiCode.trim()
    if (normalizedMbtiCode === '') return
    mbti.value = normalizedMbtiCode
    currentQuestionId.value = TOTAL_QUESTIONS
  }

  /**
   * Applique le résultat MBTI poussé par le hub (SignalR `MbtiResultChanged`).
   * @param {MbtiResultPayload} payload - Type MBTI, confiance et drapeau d'analyse en cours.
   * @returns {void}
   */
  function applyMbtiResult(payload: MbtiResultPayload): void {
    setAnalysisPending(payload.analysisPending)
    if (payload.analysisPending) return
    hydrateFromAnalysis(payload.type)
  }

  /**
   * Écrit `analysisPending` et le repasse à `false` au bout du délai max, sauf si le back le confirme de nouveau entre-temps.
   * @param {boolean} isPending - `true` tant que le back n'a pas calculé le résultat du test.
   * @returns {void}
   */
  function setAnalysisPending(isPending: boolean): void {
    analysisPending.value = isPending

    if (analysisPendingTimeoutId) {
      clearTimeout(analysisPendingTimeoutId)
      analysisPendingTimeoutId = null
    }

    if (isPending) {
      analysisPendingTimeoutId = setTimeout(
        (): void => setAnalysisPending(false),
        MAX_ANALYSIS_WAIT_MS,
      )
    }
  }

  /** Reset complet (pour refaire le test) */
  function reset(): void {
    mbti.value = null
    currentQuestionId.value = null
    traits.value = null
    forces.value = null
    shadow.value = null
    shadowNote.value = null
    status.value = 'idle'
    isPersonalityReportPending.value = false
    setAnalysisPending(false)
  }

  return {
    // State
    mbti,
    currentQuestionId,
    traits,
    forces,
    shadow,
    shadowNote,
    status,
    isPersonalityReportPending,
    analysisPending,
    // Computed
    progressPct,
    testState,
    TOTAL_QUESTIONS,
    // Actions
    fetch,
    tryRevealPersonalityReport,
    clearPersonalityReportPendingIfReady,
    setCurrentQuestion,
    setMbtiResult,
    hydrateFromAnalysis,
    applyMbtiResult,
    setAnalysisPending,
    reset,
  }
})
